import { create } from "zustand";
import { getAccountPrivacy, updateAccountPrivacy } from "@/api/privacy";
import {
  getPrivacyAccount,
  subscribePrivacyConsent,
} from "@/lib/privacy-consent";

type UsageDataState = {
  /** The account's server-side usage data choice; null until it is read. */
  enabled: boolean | null;
  failed: boolean;
  load: () => Promise<void>;
  setEnabled: (enabled: boolean) => Promise<void>;
  clear: () => void;
};

/** Only the newest read or write may settle the shown value. */
let generation = 0;
/** The last value the server confirmed; a failed write falls back to it. */
let confirmed: boolean | null = null;

export const useUsageDataStore = create<UsageDataState>((set) => ({
  enabled: null,
  failed: false,
  load: async () => {
    const run = ++generation;
    try {
      const { usage_data } = await getAccountPrivacy();
      confirmed = usage_data;
      if (run === generation) set({ enabled: usage_data, failed: false });
    } catch {
      if (run === generation) set({ failed: true });
    }
  },
  setEnabled: async (enabled) => {
    const run = ++generation;
    set({ enabled, failed: false });
    try {
      const { usage_data } = await updateAccountPrivacy({
        usage_data: enabled,
      });
      confirmed = usage_data;
      if (run === generation) set({ enabled: usage_data });
    } catch {
      if (run === generation) set({ enabled: confirmed, failed: true });
    }
  },
  clear: () => {
    generation += 1;
    confirmed = null;
    set({ enabled: null, failed: false });
  },
}));

// Every sign-out and account switch, in any window, forgets the old choice.
let account = getPrivacyAccount();
subscribePrivacyConsent(() => {
  const next = getPrivacyAccount();
  if (next === account) return;
  account = next;
  useUsageDataStore.getState().clear();
});
